import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import WhatsAppButton from "@/components/feature/WhatsAppButton";

export default function VendorsPage() {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.documentElement.lang = i18n.language;
    window.scrollTo(0, 0);
  }, [i18n.language]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full min-h-[80vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Cameroonian%20woman%20street%20food%20vendor%20proudly%20standing%20behind%20colorful%20food%20stall%20with%20traditional%20dishes%20fresh%20ingredients%20arranged%20beautifully%20warm%20natural%20lighting%20confident%20smile%20authentic%20marketplace%20Douala%20scene%20editorial%20portrait%20photography&width=1600&height=900&seq=vendors-hero-v2&orientation=landscape"
              alt=""
              className="w-full h-full object-cover object-center"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/65 via-foreground-950/45 to-foreground-950/75"></div>
          </div>
          <div className="relative w-full px-4 md:px-6 lg:px-10 pt-24 md:pt-32 pb-16 md:pb-20">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center max-w-7xl mx-auto">
              <div>
                <span className="inline-block px-4 py-1.5 rounded-full bg-accent-500/20 backdrop-blur-sm border border-accent-400/30 text-accent-200 text-xs font-semibold uppercase tracking-wider mb-6">
                  {t("nav.vendors")}
                </span>
                <h1 className="font-heading text-4xl sm:text-5xl md:text-6xl font-extrabold text-background-50 mb-6 leading-tight tracking-tight">
                  {t("vendors.title")}
                </h1>
                <p className="text-lg md:text-xl text-background-200/90 leading-relaxed mb-8">
                  {t("vendors.subtitle")}
                </p>
                <Link
                  to="/waitlist"
                  className="inline-flex items-center gap-2 px-8 py-4 bg-accent-500 text-background-50 rounded-full text-sm font-semibold hover:bg-accent-600 transition-all hover:scale-105 shadow-lg shadow-accent-500/25 whitespace-nowrap cursor-pointer"
                >
                  {t("vendors.cta")}
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-arrow-right-line"></i>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits */}
        <section className="py-20 md:py-28 bg-background-50">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-14 md:mb-20">
              <span className="inline-block px-4 py-1.5 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold uppercase tracking-wider mb-5">
                Avantages
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5">
                Pourquoi vendre avec Mayouk ?
              </h2>
              <div className="w-20 h-1 bg-accent-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5 md:gap-6">
              {[
                { icon: "ri-eye-line", title: t("vendors.benefit1_title"), desc: t("vendors.benefit1_desc") },
                { icon: "ri-group-line", title: t("vendors.benefit2_title"), desc: t("vendors.benefit2_desc") },
                { icon: "ri-rocket-line", title: t("vendors.benefit3_title"), desc: t("vendors.benefit3_desc") },
                { icon: "ri-money-dollar-circle-line", title: t("vendors.benefit4_title"), desc: t("vendors.benefit4_desc") },
                { icon: "ri-smartphone-line", title: t("vendors.benefit5_title"), desc: t("vendors.benefit5_desc") },
              ].map((benefit, index) => (
                <div key={index} className="group bg-background-100 rounded-2xl p-6 border border-background-200 hover:shadow-md hover:border-accent-200 transition-all duration-300 text-center">
                  <div className="w-12 h-12 rounded-xl bg-accent-100 flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                    <div className="w-6 h-6 flex items-center justify-center">
                      <i className={`${benefit.icon} text-accent-600 text-xl`}></i>
                    </div>
                  </div>
                  <h3 className="font-heading text-sm font-semibold text-foreground-950 mb-2">
                    {benefit.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">
                    {benefit.desc}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section className="py-20 md:py-28 bg-background-100">
          <div className="w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto">
            <div className="text-center mb-14 md:mb-20">
              <span className="inline-block px-4 py-1.5 rounded-full bg-primary-100 text-primary-800 text-xs font-semibold uppercase tracking-wider mb-5">
                Processus
              </span>
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 mb-5">
                Comment ca marche
              </h2>
              <div className="w-20 h-1 bg-primary-500 rounded-full mx-auto"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {[
                {
                  step: "1",
                  title: "Inscrivez-vous",
                  desc: "Creez votre compte vendeur en 5 minutes avec notre processus simple.",
                  img: "https://readdy.ai/api/search-image?query=Cameroonian%20food%20vendor%20registering%20on%20tablet%20device%20warm%20indoor%20setting%20friendly%20expression%20modern%20African%20small%20business%20scene%20editorial%20photography&width=500&height=500&seq=vendor-step1-v2&orientation=squarish"
                },
                {
                  step: "2",
                  title: "Ajoutez votre menu",
                  desc: "Telechargez vos plats, fixez vos prix et personnalisez votre profil vendeur.",
                  img: "https://readdy.ai/api/search-image?query=Smartphone%20displaying%20food%20menu%20app%20interface%20with%20photos%20of%20traditional%20Cameroonian%20dishes%20clean%20modern%20design%20warm%20lighting%20editorial%20product%20photography&width=500&height=500&seq=vendor-step2-v2&orientation=squarish"
                },
                {
                  step: "3",
                  title: "Recevez les commandes",
                  desc: "Soyez notifie instantanement quand les clients passent commande de votre menu.",
                  img: "https://readdy.ai/api/search-image?query=Cameroonian%20food%20vendor%20looking%20at%20smartphone%20receiving%20new%20order%20notification%20happy%20expression%20warm%20lighting%20in%20small%20restaurant%20kitchen%20authentic%20scene%20editorial%20photography&width=500&height=500&seq=vendor-step3-v2&orientation=squarish"
                },
                {
                  step: "4",
                  title: "Soyez paye",
                  desc: "Recevez vos paiements directement sur votre compte Mobile Money apres chaque livraison.",
                  img: "https://readdy.ai/api/search-image?query=Happy%20Cameroonian%20woman%20checking%20Mobile%20Money%20balance%20on%20phone%20MTN%20Orange%20Money%20positive%20expression%20warm%20lighting%20success%20concept%20editorial%20photography&width=500&height=500&seq=vendor-step4-v2&orientation=squarish"
                },
              ].map((item, index) => (
                <div key={index} className="group relative">
                  <div className="absolute -top-3 -left-3 z-20 w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center shadow-lg">
                    <span className="text-sm font-bold text-background-50">{item.step}</span>
                  </div>
                  <div className="bg-background-50 rounded-2xl overflow-hidden border border-background-200 hover:border-primary-200 transition-all duration-500 hover:shadow-lg h-full">
                    <div className="h-44 overflow-hidden">
                      <img
                        src={item.img}
                        alt={item.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                      />
                    </div>
                    <div className="p-5 md:p-6">
                      <h3 className="font-heading text-base font-semibold text-foreground-950 mb-2">
                        {item.title}
                      </h3>
                      <p className="text-sm text-foreground-600 leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20 md:py-28 overflow-hidden bg-accent-500">
          <div className="absolute inset-0 bg-gradient-to-l from-accent-600/50 to-transparent"></div>
          <div className="relative w-full px-4 md:px-6 lg:px-10 max-w-7xl mx-auto text-center">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-background-50 mb-5">
              Pret a developper votre activite ?
            </h2>
            <p className="text-base md:text-lg text-background-200/90 mb-8 max-w-xl mx-auto">
              Rejoignez des centaines de vendeurs locaux deja presents sur la plateforme Mayouk.
            </p>
            <Link
              to="/waitlist"
              className="inline-flex items-center gap-2 px-8 py-4 bg-background-50 text-accent-600 rounded-full text-sm font-semibold hover:bg-background-100 transition-all hover:scale-105 shadow-lg whitespace-nowrap cursor-pointer"
            >
              {t("vendors.cta")}
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line"></i>
              </div>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}